import React from "react";
import { Link, useParams } from "react-router-dom";
import { ShieldCheck, MapPin, Phone, Mail, ArrowRight, Star } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import SeoHead from "@/components/SeoHead";
import { Button } from "@/components/ui/button";
import { useBrand, useMarket, useTenantPath } from "@/lib/brand";
import { mediaUrl } from "@/lib/api";
import { buildLocalBusinessJsonLd } from "@/lib/seo";

/**
 * AgencyAbout — the tenant's About page.
 *
 * Reads from the tenant's brand blob:
 *   business.name / tagline / hero_headline / hero_sub
 *   assets.hero_image_url / hero_caption
 *   about.paragraphs (list of paragraphs) / about.mission
 *   trust_badges / regulatory.footer_lines / testimonial
 *   team (list of {name, role, photo_url, bio})
 *   areas_served (list of strings)
 */
export default function AgencyAbout() {
  const { slug: urlSlug } = useParams();
  const brand = useBrand();
  const market = useMarket();
  const isUs = market === "us";
  const tenantPath = useTenantPath();
  const biz = brand.business || {};
  const tenantSlug = urlSlug || brand.tenant_slug || "foundation";
  const heroImg = brand.assets?.hero_image_url ? mediaUrl(brand.assets.hero_image_url) : "";
  const paragraphs = brand.about?.paragraphs || [
    `${biz.name || "We"} are a small, independent team who believe ${isUs ? "real estate" : "estate agency"} should be personal, transparent and evidence-led: the way it used to be, done with the best of what's new.`,
    `We market properties beautifully, communicate honestly, and negotiate hard for the people who trust us. Our clients come to us on recommendation, and we're proud that most of them come back.`,
  ];
  const team = brand.team || [];
  const areas = brand.areas_served || [];
  const testimonials = brand.testimonials || [];
  const partners = brand.partners?.items || [];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SeoHead
        title={biz.name ? `About ${biz.name} | ${isUs ? "Independent Brokerage" : "Independent Estate Agents"} in ${brand.regions_covered_prose || "Your Area"}` : undefined}
        description={brand.about?.paragraphs?.[0] || biz.elevator_pitch || (isUs
          ? `About ${biz.name || "our brokerage"}, a personal, local real estate brokerage serving ${brand.regions_covered_prose || "your area"}.`
          : `About ${biz.name || "our agency"}, a personal, local estate agency covering ${brand.regions_covered_prose || "your area"}.`)}
        image={heroImg}
        jsonLd={buildLocalBusinessJsonLd(brand, { imageUrl: heroImg })}
      />
      <SiteHeader />
      <main className="flex-1">
        <section className="mx-auto max-w-6xl px-6 lg:px-10 py-16 lg:py-24">
          <div className="overline">About {biz.name || "us"}</div>
          <h1 className="font-serif-display text-5xl lg:text-6xl text-primary mt-6 tracking-tight leading-[1.05] max-w-4xl">
            {brand.about?.headline || (isUs
              ? `Personal, considered real estate in ${brand.regions_covered_prose || "your area"}.`
              : `Personal, considered estate agency in ${brand.regions_covered_prose || "your area"}.`)}
          </h1>
        </section>

        <section className="border-t border-border bg-secondary/15">
          <div className="mx-auto max-w-6xl px-6 lg:px-10 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              {heroImg && (
                <img src={heroImg} alt={brand.assets?.hero_caption || biz.name} className="w-full aspect-[4/5] object-cover rounded-sm" />
              )}
              {areas.length > 0 && (
                <div className="mt-6">
                  <div className="overline">Where we work</div>
                  <ul className="mt-3 grid grid-cols-2 gap-y-1.5 text-sm text-primary">
                    {areas.map((a, i) => (
                      <li key={i} className="flex items-center gap-1.5">
                        <MapPin className="w-3 h-3 text-accent" /> {a}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div className="lg:col-span-7 space-y-6">
              <div className="overline">Our story</div>
              {paragraphs.map((p, i) => (
                <p key={i} className="text-lg leading-relaxed text-primary/85">{p}</p>
              ))}
              {brand.trust_badges?.length > 0 && (
                <div className="pt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {brand.trust_badges.map((b, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm text-primary">
                      <ShieldCheck className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                      <span>{b.text}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>

        {team.length > 0 && (
          <section className="border-t border-border">
            <div className="mx-auto max-w-6xl px-6 lg:px-10 py-16 lg:py-24">
              <div className="overline">The team</div>
              <h2 className="font-serif-display text-4xl text-primary mt-3">People, not portals.</h2>
              <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {team.map((m, i) => (
                  <div key={i} className="stagger-item">
                    {m.photo_url ? (
                      <img src={mediaUrl(m.photo_url)} alt={m.name} className="w-full aspect-square rounded-sm object-cover" />
                    ) : (
                      <div className="w-full aspect-square rounded-sm bg-secondary/40" />
                    )}
                    <div className="font-serif-display text-2xl text-primary mt-5">{m.name}</div>
                    <div className="text-xs uppercase tracking-widest text-muted-foreground mt-1">{m.role}</div>
                    {m.bio && <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{m.bio}</p>}
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {testimonials.length > 0 ? (
          <section className="border-t border-border bg-primary text-primary-foreground">
            <div className="mx-auto max-w-6xl px-6 lg:px-10 py-20">
              <div className="text-center">
                <div className="text-6xl font-serif-display text-accent/80 leading-none">"</div>
                <h2 className="font-serif-display text-3xl mt-2">Reviews from people we've helped move</h2>
              </div>
              <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {testimonials.map((t, i) => (
                  <div key={i} className="stagger-item">
                    <div className="flex gap-0.5 text-accent">
                      {Array.from({ length: 5 }).map((_, s) => <Star key={s} className="w-3.5 h-3.5 fill-current" />)}
                    </div>
                    <p className="text-sm leading-relaxed mt-4 opacity-90">"{t.quote}"</p>
                    <div className="text-xs uppercase tracking-widest text-primary-foreground/60 mt-5">
                      {t.author}{t.location ? `, ${t.location}` : ""}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        ) : brand.testimonial && (
          <section className="border-t border-border bg-primary text-primary-foreground">
            <div className="mx-auto max-w-4xl px-6 lg:px-10 py-20 text-center">
              <div className="text-6xl font-serif-display text-accent/80 leading-none">"</div>
              <p className="font-serif-display text-3xl leading-snug mt-4">
                {brand.testimonial.quote}
              </p>
              <div className="text-xs uppercase tracking-widest text-primary-foreground/60 mt-8">
                {brand.testimonial.attribution || brand.testimonial.author}
              </div>
            </div>
          </section>
        )}

        {partners.length > 0 && (
          <section className="border-t border-border">
            <div className="mx-auto max-w-6xl px-6 lg:px-10 py-16 lg:py-24">
              <div className="overline">{brand.partners?.headline || "Trusted local partners"}</div>
              {brand.partners?.intro && (
                <p className="text-muted-foreground mt-3 max-w-2xl leading-relaxed">{brand.partners.intro}</p>
              )}
              <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {partners.map((p, i) => (
                  <div key={i} className="border border-border rounded-sm p-5">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-primary font-semibold">{p.category}</div>
                    <div className="font-serif-display text-lg text-primary mt-1.5">{p.name}</div>
                    {p.location && <div className="text-xs text-muted-foreground mt-0.5">{p.location}</div>}
                    {p.blurb && <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{p.blurb}</p>}
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="border-t border-border">
          <div className="mx-auto max-w-6xl px-6 lg:px-10 py-16 lg:py-20 flex items-center justify-between flex-wrap gap-6">
            <div>
              <div className="overline">{isUs ? "Thinking of selling or renting out?" : "Thinking of selling or letting?"}</div>
              <h3 className="font-serif-display text-3xl text-primary mt-3">Have a genuine, no-pressure conversation.</h3>
            </div>
            <div className="flex gap-3">
              <Link to={tenantPath("valuation")}>
                <Button size="lg" className="rounded-sm sharp-btn">
                  {isUs ? "Home valuation" : "Free valuation"} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to={tenantPath("contact")}>
                <Button size="lg" variant="outline" className="rounded-sm border-primary/25 sharp-btn">
                  Contact us
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
